/**
 * hooks/useAuth.tsx
 * =================
 * Auth context for the app — backed by **Amazon Cognito**. The exported
 * interface is kept compatible with the previous Supabase version so the ~50
 * consumers don't need to change:
 *
 *   const { user, session, loading, roles, primaryRole, mfa, signOut } = useAuth();
 *
 * Compatibility shims:
 *  - `user.user_metadata` is synthesised (best-effort) from the Cognito user;
 *    phone/account_type aren't stored here so they read undefined and callers
 *    fall back gracefully.
 *  - `session.access_token` is retained for shape compatibility but is empty —
 *    the Cognito access token lives in an httpOnly cookie and is never exposed
 *    to JS. Browser→edge callers fetch it from /api/auth/token instead.
 *  - MFA is Cognito software-token (TOTP). Cognito gates 2FA at *login* (an
 *    enrolled user gets no session until the second factor is verified — see
 *    auth-client's redirect to /auth/mfa-verify), so any user who *has* a
 *    session is fully authenticated → AAL2. `mfa.enrolled` reflects whether
 *    TOTP is enabled. Enrollment is opt-in (/auth/mfa-setup), not forced.
 */

import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  type ReactNode,
} from "react";
import { authClient } from "@/lib/auth/auth-client";

// Mirrors the live public.app_role enum in Aurora. Only patient/doctor/admin are
// ever stored; 'provider'/'user' exist in the enum but are unused (2026-07-16).
export type UserRole = "patient" | "doctor" | "admin" | "user" | "provider";

export type MfaState = {
  aal: "aal1" | "aal2" | null;
  enrolled: boolean;
  needsSetup: boolean;
  needsVerify: boolean;
  ready: boolean;
};

/** Minimal user shape; `user_metadata` kept for backward compatibility. */
export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  user_metadata?: Record<string, any>;
}

export interface AuthSession {
  token: string;
  /** Retained for shape compatibility; empty — the Cognito access token is
   *  httpOnly and never exposed to JS (edge callers use /api/auth/token). */
  access_token: string;
  expiresAt: Date | null;
  user: AuthUser | null;
}

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  loading: boolean;
  roles: UserRole[];
  primaryRole: UserRole | null;
  mfa: MfaState;
  refreshMfa: () => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

// Signed-out / loading default: nothing required so guards pass.
const DEFAULT_MFA: MfaState = {
  aal: null,
  enrolled: false,
  needsSetup: false,
  needsVerify: false,
  ready: true,
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  roles: [],
  primaryRole: null,
  mfa: DEFAULT_MFA,
  refreshMfa: async () => {},
  signOut: async () => {},
  signInWithGoogle: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: sessionData, isPending } = authClient.useSession();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const userId = sessionData?.user?.id;

  // Resolve roles from Aurora (public.user_roles) whenever the user changes.
  useEffect(() => {
    let mounted = true;
    if (!userId) {
      setRoles([]);
      setRolesLoaded(false);
      return;
    }
    setRolesLoaded(false);
    import("@/lib/auth/roles.functions")
      .then(({ fetchUserRoles }) => fetchUserRoles({ data: { userId } }))
      .then((fetched: UserRole[]) => {
        if (!mounted) return;
        setRoles(fetched);
        setRolesLoaded(true);
      })
      .catch(() => {
        if (!mounted) return;
        setRoles(["patient"]);
        setRolesLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, [userId]);

  const refreshMfa = useCallback(async () => {
    // Re-fetch the session so user.twoFactorEnabled (and thus `mfa`) is fresh
    // right after enrollment/disable. useSession() is backed by the same store,
    // so this updates `sessionData` reactively.
    try {
      await authClient.getSession();
    } catch {
      /* transient — the reactive session will catch up on next poll */
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authClient.signOut();
    } finally {
      if (typeof window !== "undefined") localStorage.removeItem("heallexa_signup_account_type");
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const stash =
      typeof window !== "undefined"
        ? localStorage.getItem("heallexa_signup_account_type")
        : null;
    if (stash && typeof window !== "undefined") {
      sessionStorage.setItem("heallexa_pending_role", stash);
    }
    await authClient.signIn.social({ provider: "google", callbackURL: "/post-login" });
  }, []);

  const user: AuthUser | null = sessionData?.user
    ? {
        id: sessionData.user.id,
        email: sessionData.user.email,
        name: sessionData.user.name,
        image: sessionData.user.image,
        user_metadata: { full_name: sessionData.user.name ?? undefined },
      }
    : null;

  const session: AuthSession | null = sessionData?.session
    ? {
        token: sessionData.session.token,
        access_token: sessionData.session.token,
        expiresAt: sessionData.session.expiresAt
          ? new Date(sessionData.session.expiresAt)
          : null,
        user,
      }
    : null;

  // Any user holding a session has already cleared the 2FA gate at login
  // (Better Auth withholds the session until the second factor is verified),
  // so a present user is AAL2. `enrolled` mirrors the plugin's twoFactorEnabled.
  const mfa: MfaState = sessionData?.user
    ? {
        aal: "aal2",
        enrolled: !!(sessionData.user as { twoFactorEnabled?: boolean }).twoFactorEnabled,
        needsSetup: false,
        needsVerify: false,
        ready: true,
      }
    : DEFAULT_MFA;

  const primaryRole: UserRole | null = roles.includes("admin")
    ? "admin"
    : roles.includes("doctor")
    ? "doctor"
    : roles.includes("patient")
    ? "patient"
    : roles[0] ?? null;

  const loading = isPending || (!!userId && !rolesLoaded);

  return (
    <AuthContext
      value={{
        user,
        session,
        loading,
        roles,
        primaryRole,
        mfa,
        refreshMfa,
        signOut,
        signInWithGoogle,
      }}
    >
      {children}
    </AuthContext>
  );
}
